import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { grantAnalyticsConsent, revokeAnalyticsConsent } from '@/lib/analytics';
import { grantMetaConsent, revokeMetaConsent } from '@/lib/metaPixel';
import { readConsent, writeConsent } from '@/lib/consent';
import './CookieConsent.css';

export function CookieConsent(): React.JSX.Element | null {
  const location = useLocation();
  const [needsDecision, setNeedsDecision] = useState(false);
  const [show, setShow] = useState(false);

  // On mount and on route change, decide whether to surface the banner.
  // Past decisions are honoured: if the user already accepted, fire the
  // consent grant so subsequent navigations are tracked properly.
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      setNeedsDecision(false);
      return;
    }
    const stored = readConsent();
    if (stored === 'accepted') {
      grantAnalyticsConsent();
      grantMetaConsent();
      setNeedsDecision(false);
      return;
    }
    if (stored === 'rejected') {
      // Already denied by Consent Mode default; nothing to do.
      setNeedsDecision(false);
      return;
    }
    setNeedsDecision(true);
  }, [location.pathname]);

  // Slight delay before sliding in so the banner doesn't pop up at the
  // same instant as page paint.
  useEffect(() => {
    if (!needsDecision) {
      setShow(false);
      return;
    }
    const id = window.setTimeout(() => setShow(true), 500);
    return () => window.clearTimeout(id);
  }, [needsDecision]);

  if (!needsDecision) return null;

  const accept = (): void => {
    writeConsent('accepted');
    grantAnalyticsConsent();
    grantMetaConsent();
    setShow(false);
    // Unmount after the slide-down completes.
    window.setTimeout(() => setNeedsDecision(false), 360);
  };

  const reject = (): void => {
    writeConsent('rejected');
    revokeAnalyticsConsent();
    revokeMetaConsent();
    setShow(false);
    window.setTimeout(() => setNeedsDecision(false), 360);
  };

  return (
    <div
      className={`cc-banner ${show ? 'cc-show' : ''}`}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="cc-icon" aria-hidden="true">
        <Cookie size={18} />
      </div>
      <div className="cc-text">
        <p>
          <strong>We use cookies.</strong> Google Analytics and the Meta Pixel help us
          understand how visitors use this site and which ads bring them here. See our{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>
      </div>
      <div className="cc-actions">
        <button type="button" className="cc-btn cc-btn-ghost" onClick={reject}>
          Decline
        </button>
        <button type="button" className="cc-btn cc-btn-primary" onClick={accept}>
          Accept
        </button>
      </div>
    </div>
  );
}
